/**
 * ==========================================================================
 * MUNICIPAL-INSPIRED PRODUCT PAGE — JavaScript
 * Theme: ScaleX (ecomplaybook) v1.2.0
 * ==========================================================================
 *
 * Features:
 *  - Image carousel with left/right nav + thumbnails
 *  - Touch/swipe support for mobile
 *  - Variant selection (size, color, dropdowns)
 *  - Variant image switching (carousel updates on variant change)
 *  - Quantity +/- controls
 *  - Dynamic price & availability updates
 *  - Integrates with existing Shopify cart (AJAX add-to-cart)
 */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     CAROUSEL
     ----------------------------------------------------------------------- */
  class MunicipalCarousel {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      this.track = sectionEl.querySelector('.municipal-carousel__track');
      this.slides = Array.from(sectionEl.querySelectorAll('.municipal-carousel__slide'));
      this.thumbs = Array.from(sectionEl.querySelectorAll('.municipal-carousel__thumb'));
      this.prevBtn = sectionEl.querySelector('[data-carousel-prev]');
      this.nextBtn = sectionEl.querySelector('[data-carousel-next]');
      this.counterCurrent = sectionEl.querySelector('.municipal-carousel__counter-current');

      this.currentIndex = 0;
      this.totalSlides = this.slides.length;
      this.isAnimating = false;

      if (this.totalSlides <= 1) return;

      this.bindEvents();
      this.initTouch();
    }

    bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goTo(index));
      });

      // Keyboard navigation
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });
    }

    initTouch() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;
      let diffX = 0;
      const threshold = 50;

      const viewport = this.section.querySelector('.municipal-carousel__viewport');
      if (!viewport) return;

      viewport.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
        this.track.style.transition = 'none';
      }, { passive: true });

      viewport.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        diffX = e.touches[0].clientX - startX;
        const diffY = e.touches[0].clientY - startY;

        // Only handle horizontal swipes
        if (Math.abs(diffX) > Math.abs(diffY)) {
          const currentTranslate = -(this.currentIndex * 100);
          const dragPercent = (diffX / viewport.offsetWidth) * 100;
          this.track.style.transform = `translateX(${currentTranslate + dragPercent}%)`;
        }
      }, { passive: true });

      viewport.addEventListener('touchend', () => {
        isDragging = false;
        this.track.style.transition = '';

        if (Math.abs(diffX) > threshold) {
          if (diffX < 0) this.next();
          else this.prev();
        } else {
          this.goTo(this.currentIndex);
        }
        diffX = 0;
      }, { passive: true });
    }

    prev() {
      if (this.isAnimating) return;
      const newIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
      this.goTo(newIndex);
    }

    next() {
      if (this.isAnimating) return;
      const newIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
      this.goTo(newIndex);
    }

    goTo(index) {
      if (index === this.currentIndex && this.track.style.transform !== 'none') {
        // Reset if same index (e.g., failed swipe)
      }

      this.isAnimating = true;
      this.currentIndex = index;

      // Move track
      this.track.style.transform = `translateX(-${index * 100}%)`;

      // Update active states
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));

      // Update counter
      if (this.counterCurrent) {
        this.counterCurrent.textContent = index + 1;
      }

      // Scroll thumbnail into view
      if (this.thumbs[index]) {
        this.thumbs[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      setTimeout(() => { this.isAnimating = false; }, 350);
    }

    /**
     * Navigate carousel to the slide matching a given media/image ID.
     * Used by VariantManager to switch images on variant change.
     * @param {number|string} mediaId - The media ID to navigate to
     * @returns {boolean} True if a matching slide was found and navigated to
     */
    goToMediaId(mediaId) {
      if (!mediaId) return false;
      const targetId = String(mediaId);
      const targetIndex = this.slides.findIndex(
        slide => String(slide.dataset.mediaId) === targetId
      );
      if (targetIndex !== -1 && targetIndex !== this.currentIndex) {
        this.goTo(targetIndex);
        return true;
      }
      return false;
    }
  }

  /* -----------------------------------------------------------------------
     VARIANT MANAGER
     ----------------------------------------------------------------------- */
  class MunicipalVariantManager {
    constructor(sectionEl, carousel) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      this.productId = sectionEl.dataset.productId;
      this.carousel = carousel; // Reference to the carousel instance

      // Parse product JSON
      const jsonEl = document.getElementById(`MunicipalProductJSON-${this.sectionId}`);
      if (!jsonEl) return;

      try {
        this.product = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('[Municipal] Could not parse product JSON:', e);
        return;
      }

      this.variants = this.product.variants;
      this.options = this.product.options;
      this.currentVariant = this.variants.find(v => v.id === parseInt(this.getVariantIdInput().value)) || this.variants[0];

      this.bindOptionEvents();
    }

    getVariantIdInput() {
      return this.section.querySelector(`#MunicipalVariantId-${this.sectionId}`) ||
             this.section.querySelector('input[name="id"]');
    }

    bindOptionEvents() {
      // Size buttons
      this.section.querySelectorAll('.municipal-product__size-input').forEach(input => {
        input.addEventListener('change', (e) => this.onOptionChange(e));
      });

      // Color swatches
      this.section.querySelectorAll('.municipal-product__color-input').forEach(input => {
        input.addEventListener('change', (e) => this.onOptionChange(e));
      });

      // Select dropdowns
      this.section.querySelectorAll('.municipal-product__select').forEach(select => {
        select.addEventListener('change', (e) => this.onOptionChange(e));
      });
    }

    onOptionChange(e) {
      const input = e.target;
      const position = parseInt(input.dataset.optionPosition);
      const value = input.value;

      // Update active visual state for buttons
      if (input.type === 'radio') {
        const container = input.closest('.municipal-product__option');
        container.querySelectorAll('.is-active').forEach(el => el.classList.remove('is-active'));
        input.closest('label').classList.add('is-active');

        // Update option value label
        const label = container.querySelector(`[data-option-label="${this.options[position - 1]}"]`);
        if (label) label.textContent = value;
      }

      // Build current options array
      const selectedOptions = [];
      this.options.forEach((optionName, i) => {
        const pos = i + 1;
        const radio = this.section.querySelector(`input[data-option-position="${pos}"]:checked`);
        const select = this.section.querySelector(`select[data-option-position="${pos}"]`);
        if (radio) selectedOptions.push(radio.value);
        else if (select) selectedOptions.push(select.value);
      });

      // Find matching variant
      const matchedVariant = this.variants.find(variant => {
        return variant.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (matchedVariant) {
        this.currentVariant = matchedVariant;
        this.updateVariant(matchedVariant);
      }
    }

    updateVariant(variant) {
      // Update hidden variant ID input
      const idInput = this.getVariantIdInput();
      if (idInput) idInput.value = variant.id;

      // Also update any other forms with the variant ID (for Stoq/Restock Rocket)
      this.section.querySelectorAll('input[name="id"]').forEach(input => {
        input.value = variant.id;
      });

      // Update price display
      this.updatePrice(variant);

      // Update add-to-cart button
      this.updateAddToCart(variant);

      // Update URL without reload
      this.updateURL(variant);

      // ===== UPDATE CAROUSEL IMAGE =====
      // Navigate carousel to the variant's featured image
      this.updateCarouselImage(variant);

      // Dispatch event for other scripts/apps to listen to
      window.dispatchEvent(new CustomEvent('municipal:variant:change', {
        detail: { variant: variant, product: this.product, sectionId: this.sectionId }
      }));

      // Also dispatch standard Shopify variant change event for app compatibility
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variant: variant }
      }));
    }

    /**
     * Update the carousel to show the variant's featured image.
     * Matches variant.featured_image.id to slide data-media-id attributes.
     * Also falls back to matching by image src if media ID doesn't match.
     */
    updateCarouselImage(variant) {
      if (!this.carousel) return;

      // Method 1: Use variant's featured_image.id to match slide data-media-id
      if (variant.featured_image && variant.featured_image.id) {
        const found = this.carousel.goToMediaId(variant.featured_image.id);
        if (found) return;
      }

      // Method 2: Use featured_media_id (some themes use this)
      if (variant.featured_media_id) {
        const found = this.carousel.goToMediaId(variant.featured_media_id);
        if (found) return;
      }

      // Method 3: Match by image src as fallback
      if (variant.featured_image && variant.featured_image.src) {
        const variantSrc = variant.featured_image.src.replace(/https?:/, '').split('?')[0];
        const slides = this.carousel.slides;
        for (let i = 0; i < slides.length; i++) {
          const img = slides[i].querySelector('img');
          if (img) {
            const slideSrc = (img.src || img.dataset.src || '').replace(/https?:/, '').split('?')[0];
            // Compare the base filenames (handle size suffixes like _600x)
            const variantFile = variantSrc.split('/').pop().replace(/_\d+x\d*\./, '.');
            const slideFile = slideSrc.split('/').pop().replace(/_\d+x\d*\./, '.');
            if (variantFile === slideFile || slideSrc.includes(variantFile.split('.')[0])) {
              if (i !== this.carousel.currentIndex) {
                this.carousel.goTo(i);
              }
              return;
            }
          }
        }
      }
    }

    updatePrice(variant) {
      const priceContainer = this.section.querySelector(`#MunicipalPrice-${this.sectionId}`);
      if (!priceContainer) return;

      const currentPrice = priceContainer.querySelector('.municipal-product__price-current');
      const comparePrice = priceContainer.querySelector('.municipal-product__price-compare');

      if (currentPrice) {
        currentPrice.textContent = this.formatMoney(variant.price);
        currentPrice.classList.toggle('municipal-product__price-current--sale', variant.compare_at_price > variant.price);
      }

      if (comparePrice) {
        if (variant.compare_at_price > variant.price) {
          comparePrice.textContent = this.formatMoney(variant.compare_at_price);
          comparePrice.style.display = '';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    }

    updateAddToCart(variant) {
      const btn = this.section.querySelector(`#MunicipalAddToCart-${this.sectionId}`);
      if (!btn) return;

      // Check if Stoq has modified this button (has restock-rocket classes).
      // If so, only update disabled state and let Stoq manage the label.
      const isStoqManaged = btn.classList.contains('restock-rocket-preorder-button');

      if (variant.available) {
        btn.disabled = false;
        if (!isStoqManaged) {
          // Only update label if Stoq hasn't taken over the button
          const label = btn.querySelector('.municipal-product__add-label, .main-atc__label');
          if (label) {
            label.textContent = 'ADD TO CART';
          } else {
            btn.innerHTML = '<span class="municipal-product__add-label">ADD TO CART</span> <svg class="municipal-product__add-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5.5 13.5C5.5 13.7761 5.27614 14 5 14C4.72386 14 4.5 13.7761 4.5 13.5C4.5 13.2239 4.72386 13 5 13C5.27614 13 5.5 13.2239 5.5 13.5Z" stroke="currentColor"/><path d="M12.5 13.5C12.5 13.7761 12.2761 14 12 14C11.7239 14 11.5 13.7761 11.5 13.5C11.5 13.2239 11.7239 13 12 13C12.2761 13 12.5 13.2239 12.5 13.5Z" stroke="currentColor"/><path d="M1 1H3L3.68 4M3.68 4H15L12 10H5L3.68 4Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          }
        }
      } else {
        btn.disabled = true;
        if (!isStoqManaged) {
          const label = btn.querySelector('.municipal-product__add-label, .main-atc__label');
          if (label) {
            label.textContent = 'SOLD OUT';
          } else {
            btn.textContent = 'SOLD OUT';
          }
        }
      }
    }

    updateURL(variant) {
      if (!variant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ path: url.href }, '', url.href);
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }
  }

  /* -----------------------------------------------------------------------
     QUANTITY CONTROLS
     ----------------------------------------------------------------------- */
  function initQuantityControls(sectionEl) {
    const minusBtn = sectionEl.querySelector('[data-qty-minus]');
    const plusBtn = sectionEl.querySelector('[data-qty-plus]');
    const input = sectionEl.querySelector('.municipal-product__quantity-input');

    if (!minusBtn || !plusBtn || !input) return;

    minusBtn.addEventListener('click', () => {
      const current = parseInt(input.value) || 1;
      if (current > 1) input.value = current - 1;
    });

    plusBtn.addEventListener('click', () => {
      const current = parseInt(input.value) || 1;
      input.value = current + 1;
    });

    input.addEventListener('change', () => {
      if (parseInt(input.value) < 1 || isNaN(parseInt(input.value))) {
        input.value = 1;
      }
    });
  }

  /* -----------------------------------------------------------------------
     STOQ PREORDER CHECKBOX — FALLBACK VALIDATION
     -----------------------------------------------------------------------
     Stoq (Restock Rocket) normally hooks into the <product-form> custom
     element's submit handler to show its preorder acknowledgement popup.
     This fallback ensures validation still works even if Stoq's own
     interception fails (e.g., timing issues, or if the theme's product-form
     web component hasn't fully initialized before Stoq runs).

     How it works:
       1. Listen for the form 'submit' event in the CAPTURING phase
          (runs before Stoq's bubble-phase listener).
       2. Check if the Stoq acknowledgement checkbox exists AND is unchecked.
       3. If unchecked, prevent the form from submitting.
       4. Programmatically click the submit button to trigger Stoq's own
          button click handler, which shows the popup modal.
     ----------------------------------------------------------------------- */
  function initStoqFallbackValidation(sectionEl) {
    const form = sectionEl.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    let isFallbackRetrigger = false;

    form.addEventListener('submit', function (e) {
      // Skip if this is our own programmatic re-trigger
      if (isFallbackRetrigger) {
        isFallbackRetrigger = false;
        return;
      }

      // Check if Stoq's acknowledgement checkbox exists (injected by Stoq for preorder products)
      const stoqCheckbox = form.querySelector('.restock-rocket-acknowledge-checkbox-input');
      if (!stoqCheckbox) return; // Not a preorder product — let the form submit normally

      // If the checkbox is NOT checked, block form submission
      if (!stoqCheckbox.checked) {
        e.preventDefault();
        e.stopImmediatePropagation();

        // Try to trigger Stoq's own popup by clicking the submit button.
        // Stoq typically adds a click handler on the button that shows the modal.
        // Use a flag so our listener doesn't catch the re-triggered submit.
        const submitBtn = form.querySelector('.product-form__submit');
        if (submitBtn) {
          setTimeout(function () {
            isFallbackRetrigger = true;
            submitBtn.click();
            // Reset flag after a tick in case click didn't trigger submit
            setTimeout(function () { isFallbackRetrigger = false; }, 50);
          }, 10);
        }
        return false;
      }
      // Checkbox IS checked — allow the form to submit normally
    }, true); // 'true' = capturing phase, runs before bubble-phase listeners
  }

  /* -----------------------------------------------------------------------
     INITIALIZATION
     ----------------------------------------------------------------------- */
  function init() {
    const sections = document.querySelectorAll('.municipal-product');

    sections.forEach(section => {
      try {
        const carousel = new MunicipalCarousel(section);
        new MunicipalVariantManager(section, carousel);
        initQuantityControls(section);
        initStoqFallbackValidation(section);
      } catch (err) {
        console.error('[Municipal Product] Initialization error:', err);
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section events (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.municipal-product');
    if (section) {
      try {
        const carousel = new MunicipalCarousel(section);
        new MunicipalVariantManager(section, carousel);
        initQuantityControls(section);
        initStoqFallbackValidation(section);
      } catch (err) {
        console.error('[Municipal Product] Section reload error:', err);
      }
    }
  });

})();
